import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Divider, Flex, Form, Input, InputNumber, Select, Switch } from 'antd';
import { GoogleAutocomplete, SelectForemanHelpers } from 'components/UI';
import dayjs from 'dayjs';
import { IUserRoles, IWork, IWorkState } from 'models';
import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { SlotInfo } from 'react-big-calendar';
import { userStore } from 'stores';
import {
	bedroomS,
	heavyItemsS,
	isCash,
	isWeekend,
	moversS,
	packingS,
	paymentS,
	referralS,
	statusSAdmin,
	statusSManager,
	timeS,
	truckSizeS,
	trucksS,
	typeOfResS
} from 'utils';

const { Item, useWatch, useForm } = Form;
const { Option } = Select;

interface CreateJobFormProps {
	onFinish: (values: IWork) => void
	work?: IWork,
	slot?: SlotInfo | null,
	queryKey: any[],
}

export interface CreateWorkFormRef {
	submitForm: () => void;
	getValues: () => IWork;
}

export const CreateWorkForm = forwardRef(({ onFinish, work, slot }: CreateJobFormProps, ref) => {
	const [form] = useForm<IWork>();
	const [deposit, showDeposit] = useState(!!work?.acf.deposit || false);
	const moversCount = useWatch<string>(['acf', 'customer_info', 'movers'], form);
	const time = useWatch<string>(['acf', 'customer_info', 'time'], form);
	const payment = useWatch<string>(['acf', 'customer_info', 'payment'], form);
	const moveDate = useWatch<string>(['acf', 'date'], form);

	useEffect(() => {
		if(!work?.acf.customer_info.result) {
			const count = parseInt(moversCount);
			const result = userStore.calculateHourlyRate(count, isWeekend(moveDate), isCash(payment));
			form.setFieldsValue({
				acf: { customer_info: { result: isNaN(result) ? '0' : result.toString() } }
			});
		}
	}, [form, moveDate, moversCount, payment, work?.acf.customer_info.result]);

	useImperativeHandle(ref, () => ({
		submitForm() {
			form.submit();
		},
		getValues() {
			return form.getFieldsValue();
		}
	}));

	const values = useMemo(() => {
		if (slot) {
			return {
				acf: {
					date: dayjs(slot.start),
					state: IWorkState.QUOTE,
					customer_info: {
						truck_fee: userStore.prices?.truckFee.toString(),
						time: dayjs(slot.start).format('HH:mm')
					}
				},
				author: userStore.data?.id
			};
		} else if (work) {
			const workersArr = work?.acf?.foreman_info?.workers || [];
			const foreman = workersArr?.filter(el => el.worker_role === IUserRoles.FOREMAN).map((el) => el.worker) || [];
			const helpers = workersArr?.filter(el => el.worker_role === IUserRoles.HELPER).map((el) => el.worker) || [];
			work.acf.customer_info.small_boxes = Number(work.acf.customer_info.small_boxes);
			work.acf.customer_info.medium_boxes = Number(work.acf.customer_info.medium_boxes);
			work.acf.customer_info.wrapping_paper = Number(work.acf.customer_info.wrapping_paper);
			if (workersArr) {
				work.acf.foreman_info.workers = workersArr.map(el => ({ ...el, salary: Number(el.salary) }));
			}
			return {
				acf: {
					...work.acf,
					state: work.acf.state,
					date: dayjs(work.acf.date)
				},
				author: work.author,
				id: work.id,
				foreman,
				helpers
			};
		} else {
			return {
				acf: {
					state: IWorkState.QUOTE,
					customer_info: {
						truck_fee: userStore.prices?.truckFee.toString()
					}
				},
				author: userStore.data?.id
			};
		}
	}, [slot, work]);

	const setAddress = (field: string, name: number, full_address?: string, zip?: string) => {
		const i = form.getFieldsValue().acf.customer_info;
		const key = field === 'pickup' ? 'pickup_address' : 'dropoff_address';
		const arr = i[key].map((el, index) => (index === name ? { ...el, full_address, zip } : el));
		form.setFieldsValue({ acf: { customer_info: { [key]: arr } } });
	};

	return (
		<Form
			form={form}
			scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
			name="addOrder"
			autoComplete="off"
			layout="horizontal"
			initialValues={{
				...values,
				status: 'publish'
			}}
			onFinish={onFinish}
			key={work?.id}
		>
			<Item name="author" label="Manager">
				<Select options={userStore.staff.map(el => ({ label: el.name, value: el.id }))} disabled={!userStore.isAdmin} />
			</Item>
			<Item 
				name={['acf', 'state']}
				label="Status"
				rules={[{ required: true }]}
			>
				<Select 
					options={userStore.isAdmin ? statusSAdmin : statusSManager} 
					disabled={!userStore.isAdmin && !!work}
				/>
			</Item>
			<SelectForemanHelpers form={form} work={work} />
			<Item name={['acf', 'foreman_info', 'truck']} label="Trucks" hidden={!work}>
				<Select mode="multiple" options={trucksS} disabled={!userStore.isAdmin}/>
			</Item>
			{work && (
				<Item name="id" hidden>
					<Input prefix="Order:" disabled />
				</Item>
			)}
			<Item name={['acf', 'date']} label="Date">
				<DatePicker format="MM/DD/YYYY" />
			</Item>
			<Item name={['acf', 'customer_info', 'time']} label="Start time">
				<Select options={timeS} onChange={(val) => {
					const findNext = timeS.findIndex((el) => el.value === val) + 1;
					form.setFieldValue(['acf', 'customer_info', 'end_time'], timeS[findNext].value);
				}}/>
			</Item>
			<Item name={['acf', 'customer_info', 'end_time']} label="End time">
				<Select options={timeS.slice(timeS.findIndex(el => el.value === time))} />
			</Item>
			<Item name={['acf', 'customer_info', 'movers']} label="Crew size">
				<Select options={moversS} />
			</Item>
			<Item name={['acf', 'customer_info', 'payment']} label="Payment">
				<Select options={paymentS} />
			</Item>
			<Item name={['acf', 'customer_info', 'result']} label="Hourly Rate">
				<Input prefix="$" />
			</Item>
			<Item name={['acf', 'customer_info', 'truck_fee']} label="Service Fee">
				<Input prefix="$" />
			</Item>
			<Item name={['acf', 'customer_info', 'bedroom']} label="Moving size">
				<Select options={bedroomS} />
			</Item>
			<Item name={['acf', 'customer_info', 'typeofresidency']} label="Type of residency">
				<Select options={typeOfResS} />
			</Item>
			<Item name={['acf', 'customer_info', 'truck']} label="Truck size">
				<Select options={truckSizeS} />
			</Item>
			<Item name={['acf', 'customer_info', 'heavyItems']} label="Have Items more then 300lb">
				<Select options={heavyItemsS} />
			</Item>
			<Divider />
			<Form.List name={['acf', 'customer_info', 'pickup_address']}>
				{(fields, { add, remove }) => (
					<>
						{!fields.length && <Button
							type="primary"
							onClick={() => add()}
						>
							Add pickup address
						</Button>
						}
						{fields.map((field) => (
							<div key={field.name}>
								<GoogleAutocomplete
									name={field.name}
									setAddress={setAddress}
									field="pickup"
								/>
								<Flex justify="space-between">
									<Item
										key="unit"
										name={[field.name, 'unit']}
										label="Unit"
									>
										<Input placeholder="Unit" />
									</Item>
									<Item
										key="zip"
										name={[field.name, 'zip']}
										label="Zip"
									>
										<Input placeholder="Zip" />
									</Item>
									<Button
										style={{ width: 60 }}
										type="primary"
										onClick={() => remove(field.name)}
										icon={<MinusCircleOutlined />}
										danger />
									<Button
										style={{ width: 60 }}
										type="primary"
										onClick={() => add()}
										icon={<PlusOutlined />} />
								</Flex>
							</div>
						))}
					</>
				)}
			</Form.List>
			<Divider />
			<Form.List name={['acf', 'customer_info', 'dropoff_address']}>
				{(fields, { add, remove }) => (
					<>
						{!fields.length && <Button
							type="primary"
							onClick={() => add()}
						>
							Add dropoff address
						</Button>
						}
						{fields.map(({ key, name, ...restField }) => (
							<Fragment key={key}>
								<GoogleAutocomplete
									name={name}
									setAddress={setAddress}
									field="dropoff"
								/>
								<Flex justify="space-between">
									<Item
										{...restField}
										name={[name, 'unit']}
										label="Unit"
									>
										<Input placeholder="Unit" />
									</Item>
									<Item
										{...restField}
										name={[name, 'zip']}
										label="Zip"
									>
										<Input placeholder="Zip" />
									</Item>
									<Button
										style={{ width: 60 }}
										type="primary"
										onClick={() => remove(name)}
										icon={<MinusCircleOutlined />}
										danger
									/>
									<Button
										style={{ width: 60 }}
										type="primary"
										onClick={() => add()}
										icon={<PlusOutlined />}
									/>
								</Flex>
							</Fragment>
						))}
					</>
				)}
			</Form.List>
			<Divider />
			<Item name={['acf', 'customer_info', 'packing']} label="Packing">
				<Select options={packingS} />
			</Item>
			<Item name={['acf', 'customer_info', 'supplies']} label="Supplies">
				<Select>
					<Option value="yes">Yes</Option>
					<Option value="no">No</Option>
				</Select>
			</Item>
			<Item name={['acf', 'customer_info', 'small_boxes']} label="Small Box">
				<InputNumber />
			</Item>
			<Item name={['acf', 'customer_info', 'medium_boxes']} label="Medium boxes">
				<InputNumber />
			</Item>
			<Item name={['acf', 'customer_info', 'wrapping_paper']} label="Wrapping paper">
				<InputNumber />
			</Item>
			<Item
				name={['acf', 'customer_info', 'customer_name']}
				label="Customer name"
				rules={[{ required: true }]}
			>
				<Input />
			</Item>
			<Item name={['acf', 'customer_info', 'customer_phone']} label="Customer phone" rules={[
				{ required: true }, {
					pattern: /^\d{10}$/,
					message: 'Phone must be 10 digits'
				}]}>
				<Input minLength={10} maxLength={10}/>
			</Item>
			<Item rules={[{ type: 'email' }]} name={['acf', 'customer_info', 'customer_email']} label="Customer email">
				<Input />
			</Item>
			<Item name={['acf', 'customer_info', 'contact_name']} label="Contact name">
				<Input />
			</Item>
			<Item name={['acf', 'customer_info', 'contact_phone']} label="Contact phone">
				<Input />
			</Item>
			<Item name={['acf', 'customer_info', 'contact_email']} label="Contact email">
				<Input />
			</Item>
			<Item name={['acf', 'customer_info', 'howfrom']} label="How find us">
				<Select options={referralS} />
			</Item>
			<Item label="Ask deposit?">
				<Switch 
					unCheckedChildren="No" 
					checkedChildren={work?.acf.paid ? 'Paid' : 'Yes'} 
					value={deposit}
					disabled={work?.acf.paid}
					onChange={(checked) => {
						showDeposit(checked);
						const bedroom = form.getFieldValue(['acf', 'customer_info', 'bedroom']);
						const defaultDeposit = parseInt(bedroom) > 2 ? 200 : 100;
						form.setFieldValue(['acf', 'deposit'], checked ? defaultDeposit : 0);
					}}/>
			</Item>
			<Item name={['acf', 'deposit']} label="Deposit" hidden={!deposit}>
				<InputNumber
					className="disabled-input"
					prefix="$"
					disabled={work?.acf.paid}
					changeOnWheel={false}
					controls={false}
					min={0}
				/>
			</Item>
			{(userStore.isAdmin && !work?.acf.paid) && (
				<Item name={['acf', 'paid']} label="Deposit paid" valuePropName="checked">
					<Switch unCheckedChildren="No" checkedChildren="Yes" />
				</Item>
			)}
			<Item name={['acf', 'customer_info', 'note']} label="Customer notes">
				<Input.TextArea rows={3} />
			</Item>
			<Item name={['acf', 'foreman_info', 'notes']} label="Notes for foreman">
				<Input.TextArea rows={3} />
			</Item>

			<Item name="status" hidden>
				<Input />
			</Item>
		</Form>
	);
});
